import { trigger, transition, style, animate, query, stagger, keyframes } from '@angular/animations';

export const fadeInOut = trigger('fadeInOut', [
  transition(':enter', [
    style({ opacity: 0 }),
    animate('300ms ease-in', style({ opacity: 1 }))
  ]),
  transition(':leave', [
    animate('300ms ease-out', style({ opacity: 0 }))
  ])
]);

export const slideInOut = trigger('slideInOut', [
  transition(':enter', [
    style({ transform: 'translateY(100%)', opacity: 0 }),
    animate('300ms ease-out', style({ transform: 'translateY(0)', opacity: 1 }))
  ]),
  transition(':leave', [
    animate('300ms ease-in', style({ transform: 'translateY(100%)', opacity: 0 }))
  ])
]);

/**
 * The attractions banner's slide up from / down to the bottom edge (Android's
 * `AttractionsBannerSlideMs`). It arrives as a new surface entering from off-screen, in the same
 * direction the content is moving, because scrolling is what reveals it.
 *
 * 280ms `cubic-bezier(0.4, 0, 0.2, 1)` is Compose's FastOutSlowIn, and the same numbers are on
 * `.floating-cart`'s `transition: bottom` so the cart rides with the banner. Deliberately NOT the
 * house settle easing `cubic-bezier(0.16, 1, 0.3, 1)` — that curve is ~90% complete at 30% of its
 * duration, so the cart would arrive long before the banner it is stacking on. Deliberately not
 * `slideInOut` either: its 300ms ease-out/ease-in pair would desync the two in both directions.
 */
export const attractionsSlide = trigger('attractionsSlide', [
  transition(':enter', [
    style({ transform: 'translateY(100%)', opacity: 0 }),
    // Duration is a param so the first reveal can slide slower (560ms) while every later appearance
    // keeps the default 280ms. Only :enter is parameterised; :leave always uses the normal 280ms.
    animate('{{ duration }} cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateY(0)', opacity: 1 }))
  ], { params: { duration: '280ms' } }),
  transition(':leave', [
    animate('280ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateY(100%)', opacity: 0 }))
  ])
]);

export const listAnimation = trigger('listAnimation', [
  transition('* <=> *', [
    query(':enter', [
      style({ opacity: 0, transform: 'translateY(50px)' }),
      stagger('50ms', [
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ], { optional: true })
  ])
]);

export const scaleInOut = trigger('scaleInOut', [
  transition(':enter', [
    style({ transform: 'scale(0.8)', opacity: 0 }),
    animate('300ms ease-out', style({ transform: 'scale(1)', opacity: 1 }))
  ]),
  transition(':leave', [
    animate('300ms ease-in', style({ transform: 'scale(0.8)', opacity: 0 }))
  ])
]);

export const pageTransition = trigger('pageTransition', [
  transition(':enter', [
    style({ opacity: 0, transform: 'translateX(100%)' }),
    animate('300ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
  ]),
  transition(':leave', [
    animate('300ms ease-in', style({ opacity: 0, transform: 'translateX(-100%)' }))
  ])
]);

export const bounceInOut = trigger('bounceInOut', [
  transition(':enter', [
    animate('500ms cubic-bezier(0.68, -0.55, 0.265, 1.55)', keyframes([
      style({ opacity: 0, transform: 'scale(0.3)', offset: 0 }),
      style({ opacity: 0.5, transform: 'scale(1.1)', offset: 0.7 }),
      style({ opacity: 1, transform: 'scale(1)', offset: 1.0 })
    ]))
  ]),
  transition(':leave', [
    animate('300ms ease-in', style({ opacity: 0, transform: 'scale(0.3)' }))
  ])
]); 